import { ImageMagick, initializeImageMagick, MagickFormat } from '@imagemagick/magick-wasm';

import { Asset } from '@rover/types/src/ImagePicker';

import { Logger } from '../modules/Logging';
import { MediaFile } from '../modules/Media';

let $magickReady: Promise<void> | null = null;

export function getWasmUrl(): URL | undefined {
  if (typeof import.meta !== 'undefined' && import.meta.url) {
    return new URL('@imagemagick/magick-wasm/magick.wasm', import.meta.url);
  }
  return undefined;
}

export async function ensureMagick(): Promise<void> {
  if ($magickReady) return $magickReady;
  $magickReady = (async () => {
    const wasmUrl = getWasmUrl();
    if (!wasmUrl) throw new Error('Failed to get WASM URL');
    const wasmBytes = await (await fetch(wasmUrl)).arrayBuffer();
    await initializeImageMagick(wasmBytes);
  })();
  return $magickReady;
}

function fileToByteArray(file: File | Asset): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      resolve(new Uint8Array(arrayBuffer));
    };
    reader.onerror = reject;

    // Handle both File and Asset types
    if (file instanceof File) {
      reader.readAsArrayBuffer(file);
    } else {
      // Asset type - convert to Blob first
      reader.readAsArrayBuffer(file as unknown as Blob);
    }
  });
}

export async function toJpeg(mediaFile: MediaFile, quality = 85): Promise<File> {
  await ensureMagick();

  const { file, name } = mediaFile;

  const bytes = await fileToByteArray(file);

  let out: File | undefined;

  try {
    ImageMagick.read(bytes, (img) => {
      img.quality = quality; // eslint-disable-line no-param-reassign
      img.autoOrient(); // Ensure orientation is preserved
      img.strip();
      img.write(MagickFormat.Jpeg, (data) => {
        const ab = data.slice().buffer;
        out = new File([ab], name, { type: 'image/jpeg' });
      });
    });
  } catch (e) {
    Logger.error(Error(`magick-wasm: toJpeg failed for file ${name}: ${e}`));
    throw e;
  }

  if (!out) {
    throw new Error('magick-wasm: read/write did not produce output');
  }

  return out;
}
